import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import axios from 'axios';
import { 
  Film, 
  CheckCircle, 
  User, 
  ArrowRight, 
  ArrowLeft,
  Users,
  Video,
  TrendingUp,
  Phone,
  MapPin,
  Briefcase,
  Target,
  Shield
} from 'lucide-react';
import Nav from '../components/nav';
import { baseUrl } from '../config';

const CompleteProfile = () => {
  const [googleUser, setGoogleUser] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [userType, setUserType] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [yearsExperience, setYearsExperience] = useState('');
  const [role, setRole] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/login');
      return;
    }

    const userData = localStorage.getItem('google_user_data');
    if (userData) {
      setGoogleUser(JSON.parse(userData));
    } else {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${baseUrl}/complete-google-profile`,
        {
          phone_number: phone,
          country: location,
          user_type: userType,
          year_experience: parseInt(yearsExperience),
          role: role || null
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const { access_token, redirect_url } = response.data;
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user_dashboard', redirect_url);
      localStorage.removeItem('google_user_data');
      
      navigate(redirect_url);
      
    } catch (error) {
      setErrorMessage(
        error.response?.data?.detail || 'Failed to complete profile. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && !userType) {
      setErrorMessage('Please select your user type');
      return;
    }
    if (currentStep === 2 && (!phone || !location || !yearsExperience)) {
      setErrorMessage('Please fill in all required fields');
      return;
    }
    setErrorMessage('');
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const prevStep = () => {
    setErrorMessage('');
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const userTypes = [
    {
      value: 'enthusiast',
      icon: Users,
      title: 'Film Enthusiast',
      description: 'Actors, crew members, hair stylists, and anyone passionate about the film industry',
      gradient: 'from-slate-700 via-teal-700 to-slate-800'
    },
    {
      value: 'lead',
      icon: Video,
      title: 'Production Lead',
      description: 'Production houses, filmmakers, directors, and production professionals',
      gradient: 'from-slate-800 via-cyan-700 to-slate-900'
    },
    {
      value: 'marketing',
      icon: TrendingUp,
      title: 'Marketing Enthusiast',
      description: 'Brand collaborations, OTT platforms, media houses, and distribution partners',
      gradient: 'from-teal-800 via-slate-700 to-cyan-800'
    }
  ];

  const roleOptions = {
    enthusiast: [
      { value: 'leadActor', label: 'Lead Actor/Performer', icon: Users },
      { value: 'creativeDirector', label: 'Creative Director/Writer', icon: Film },
      { value: 'musicSoundExpert', label: 'Music/Sound Expert', icon: Video },
      { value: 'visualArtSpecialist', label: 'Visual/Art Specialist', icon: Film },
      { value: 'scriptStoryDeveloper', label: 'Script/Story Developer', icon: Film },
      { value: 'productionCoordination', label: 'Production/Coordination Lead', icon: Briefcase },
      { value: 'editingPostProduction', label: 'Editing/Post-Production', icon: Video }
    ],
    lead: [
      { value: 'direction', label: 'Direction', icon: Video },
      { value: 'cinematography', label: 'Cinematography', icon: Film },
      { value: 'editing', label: 'Editing', icon: Film },
      { value: 'sound', label: 'Sound', icon: Video }
    ],
    marketing: [
      { value: 'socialMedia', label: 'Social Media', icon: TrendingUp },
      { value: 'emailMarketing', label: 'Email Marketing', icon: TrendingUp },
      { value: 'contentCreation', label: 'Content Creation', icon: Film },
      { value: 'seo', label: 'SEO', icon: Target },
      { value: 'paidAdvertising', label: 'Paid Advertising', icon: TrendingUp }
    ]
  };

  const filmReels = [...Array(15)].map((_, i) => ({
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    rotation: Math.random() * 360,
    delay: i * 0.1
  }));

  if (!googleUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="cpf-loader-spinner w-16 h-16 border-4 border-teal-400/20 border-t-teal-400 rounded-full"></div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Complete Sign Up"
        description="Complete your ProducerBuddy account setup to start using AI-powered film production tools."
        url="https://producerbuddy.pilomation.com/complete-signup"
        robots="noindex, nofollow"
      />
      <style>{`
        @keyframes cpf-float {
          0%, 100% {
            transform: translateY(0) rotate(var(--cpf-rotation));
          }
          50% {
            transform: translateY(-20px) rotate(var(--cpf-rotation));
          }
        }

        @keyframes cpf-pulse-opacity {
          0%, 100% {
            opacity: 0.08;
          }
          50% {
            opacity: 0.15;
          }
        }

        @keyframes cpf-spin {
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes cpf-slide-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes cpf-button-hover {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-2px);
          }
        }

        @keyframes cpf-shimmer {
          0% {
            background-position: -1000px 0;
          }
          100% {
            background-position: 1000px 0;
          }
        }

        .cpf-film-reel {
          animation: cpf-float 6s ease-in-out infinite, cpf-pulse-opacity 3s ease-in-out infinite;
        }

        .cpf-loader-spinner,
        .cpf-button-spinner {
          animation: cpf-spin 1s linear infinite;
        }

        .cpf-step-content {
          animation: cpf-slide-in 0.4s ease-out;
        }

        .cpf-user-type-card {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }

        .cpf-user-type-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.1);
        }

        .cpf-role-option {
          transition: all 0.3s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
        }

        .cpf-role-option:hover {
          transform: translateX(6px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        }

        .cpf-main-card {
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
          box-shadow: 
            0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 10px 10px -5px rgba(0, 0, 0, 0.04),
            0 0 0 1px rgba(148, 163, 184, 0.1);
        }

        .cpf-primary-button {
          position: relative;
          overflow: hidden;
          box-shadow: 0 10px 15px -3px rgba(13, 148, 136, 0.3), 0 4px 6px -2px rgba(13, 148, 136, 0.2);
        }

        .cpf-primary-button:hover {
          animation: cpf-button-hover 0.6s ease-in-out infinite;
          box-shadow: 0 20px 25px -5px rgba(13, 148, 136, 0.4), 0 10px 10px -5px rgba(13, 148, 136, 0.3);
        }

        .cpf-primary-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          transition: left 0.5s;
        }

        .cpf-primary-button:hover::before {
          left: 100%;
        }

        .cpf-secondary-button {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }

        .cpf-secondary-button:hover {
          animation: cpf-button-hover 0.6s ease-in-out infinite;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.1);
        }

        .cpf-input-field {
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .cpf-input-field:focus {
          box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
        }

        .cpf-icon-gradient {
          background: linear-gradient(135deg, #0f766e 0%, #115e59 50%, #134e4a 100%);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1);
        }

        .cpf-selected-card {
          box-shadow: 
            0 0 0 3px rgba(20, 184, 166, 0.3),
            0 10px 15px -3px rgba(20, 184, 166, 0.2),
            0 4px 6px -2px rgba(20, 184, 166, 0.1);
        }
      `}</style>

      <Nav />
      
      <div className="min-h-screen flex items-center justify-center py-12 px-4 md:px-8 relative bg-gradient-to-br from-slate-100 via-teal-50 to-cyan-50 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {filmReels.map((reel, i) => (
            <div
              key={i}
              className="absolute cpf-film-reel"
              style={{
                left: reel.left,
                top: reel.top,
                '--cpf-rotation': `${reel.rotation}deg`,
                animationDelay: `${reel.delay}s`
              }}
            >
              <Film className="w-12 h-12 text-teal-300/40" />
            </div>
          ))}
        </div>

        <div className="relative max-w-4xl w-full cpf-main-card p-8 md:p-12 pb-16 rounded-[32px]">
          {isLoading && (
            <div className="absolute inset-0 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center rounded-[32px] z-50 gap-4">
              <div className="cpf-button-spinner w-16 h-16 border-4 border-teal-400/20 border-t-teal-600 rounded-full"></div>
              <p className="text-teal-700 text-lg font-semibold">Completing your profile...</p>
            </div>
          )}

          {/* Header Section */}
          <div className="text-center mb-10">
            <div className="relative w-20 h-20 md:w-24 md:h-24 mx-auto mb-6">
              {googleUser.picture ? (
                <img 
                  src={googleUser.picture} 
                  alt={googleUser.name} 
                  className="w-full h-full rounded-full object-cover border-4 border-teal-500 shadow-xl"
                />
              ) : (
                <div className="w-full h-full rounded-full border-4 border-teal-500 cpf-icon-gradient flex items-center justify-center">
                  <User className="w-12 h-12 text-white" />
                </div>
              )}
              <div className="absolute -bottom-1 -right-1 bg-teal-600 rounded-full p-1.5 border-4 border-white shadow-lg">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-slate-800 via-teal-700 to-slate-800 bg-clip-text text-transparent">
              Welcome, {googleUser.name}! 👋
            </h1>
            <p className="text-slate-700 text-base md:text-lg mb-1 font-medium">
              Signed in with <span className="text-teal-700 font-bold">{googleUser.email}</span>
            </p>
          </div>

          {errorMessage && (
            <div className="bg-blue-50 border-2 border-red-300 text-black-800 p-4 rounded-2xl mb-6 flex items-center gap-3 shadow-sm">
              <Shield className="w-5 h-5 flex-shrink-0 text-blue-600" />
              <span className="font-semibold">{errorMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Step 1: User Type Selection */}
            {currentStep === 1 && (
              <div className="cpf-step-content mb-10">
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">Choose Your Path</h2>
                  <p className="text-slate-600 text-lg font-medium">Select the category that best describes you</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                  {userTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <div
                        key={type.value}
                        onClick={() => setUserType(type.value)}
                        className={`cpf-user-type-card cursor-pointer p-6 rounded-3xl border-2 transition-all ${
                          userType === type.value
                            ? 'border-teal-500 bg-teal-50/80 cpf-selected-card'
                            : 'border-slate-200 bg-white hover:border-teal-300'
                        }`}
                      >
                        <div className={`w-16 h-16 rounded-2xl cpf-icon-gradient flex items-center justify-center mb-4 mx-auto`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2 text-center">{type.title}</h3>
                        <p className="text-sm text-slate-600 font-medium text-center leading-relaxed">{type.description}</p>
                        {userType === type.value && (
                          <div className="mt-4 flex items-center justify-center">
                            <div className="bg-teal-600 rounded-full p-1 shadow-md">
                              <CheckCircle className="w-5 h-5 text-white" />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 2: Basic Information */}
            {currentStep === 2 && (
              <div className="cpf-step-content mb-10">
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">Basic Information</h2>
                  <p className="text-slate-600 text-lg font-medium">Tell us a bit about yourself</p>
                </div>

                <div className="space-y-6 max-w-2xl mx-auto">
                  {/* Phone Number */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-slate-800 font-bold text-base">
                      <Phone className="w-5 h-5 text-teal-700" />
                      Phone Number
                      <span className="text-red-600">*</span>
                    </label>
                    <PhoneInput
                      country={'us'}
                      value={phone}
                      onChange={setPhone}
                      inputStyle={{
                        width: '100%',
                        height: '56px',
                        fontSize: '16px',
                        fontWeight: '500',
                        backgroundColor: '#ffffff',
                        border: '2px solid #cbd5e1',
                        borderRadius: '16px',
                        color: '#1e293b',
                        paddingLeft: '50px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
                      }}
                      buttonStyle={{
                        backgroundColor: '#ffffff',
                        border: '2px solid #cbd5e1',
                        borderRight: 'none',
                        borderRadius: '16px 0 0 16px'
                      }}
                      dropdownStyle={{
                        backgroundColor: '#ffffff',
                        color: '#1e293b',
                        border: '2px solid #cbd5e1',
                        borderRadius: '16px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  {/* Location */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-slate-800 font-bold text-base">
                      <MapPin className="w-5 h-5 text-teal-700" />
                      Country / Location
                      <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      className="cpf-input-field w-full px-4 py-4 bg-white border-2 border-slate-300 rounded-2xl text-slate-800 font-medium text-base placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all duration-300"
                      placeholder="e.g., United States, India, United Kingdom"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  {/* Years of Experience */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-slate-800 font-bold text-base">
                      <Briefcase className="w-5 h-5 text-teal-700" />
                      Years of Experience
                      <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="number"
                      className="cpf-input-field w-full px-4 py-4 bg-white border-2 border-slate-300 rounded-2xl text-slate-800 font-medium text-base placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all duration-300"
                      placeholder="Enter years of experience"
                      value={yearsExperience}
                      onChange={(e) => setYearsExperience(e.target.value)}
                      min="0"
                      max="50"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Role/Specialty */}
            {currentStep === 3 && (
              <div className="cpf-step-content mb-10">
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">Your Specialization</h2>
                  <p className="text-slate-600 text-lg font-medium">What's your area of expertise? (Optional)</p>
                </div>

                <div className="space-y-3 max-w-2xl mx-auto">
                  {roleOptions[userType]?.map((option) => {
                    const Icon = option.icon;
                    return (
                      <div
                        key={option.value}
                        onClick={() => setRole(option.value)}
                        className={`cpf-role-option cursor-pointer p-4 rounded-2xl border-2 transition-all flex items-center gap-4 ${
                          role === option.value
                            ? 'border-teal-500 bg-teal-50/80 shadow-lg'
                            : 'border-slate-200 bg-white hover:border-teal-300'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          role === option.value
                            ? 'cpf-icon-gradient'
                            : 'bg-slate-100'
                        }`}>
                          <Icon className={`w-6 h-6 ${role === option.value ? 'text-white' : 'text-slate-700'}`} />
                        </div>
                        <span className="text-slate-800 font-semibold flex-1">{option.label}</span>
                        {role === option.value && (
                          <CheckCircle className="w-6 h-6 text-teal-600" />
                        )}
                      </div>
                    );
                  })}
                  {/* Skip option */}
                  <div
                    onClick={() => setRole('')}
                    className={`cpf-role-option cursor-pointer p-4 rounded-2xl border-2 transition-all flex items-center gap-4 ${
                      role === ''
                        ? 'border-teal-500 bg-teal-50/80 shadow-lg'
                        : 'border-slate-200 bg-white hover:border-teal-300'
                    }`}
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-slate-100">
                      <ArrowRight className="w-6 h-6 text-slate-700" />
                    </div>
                    <span className="text-slate-800 font-semibold flex-1">Skip for now</span>
                    {role === '' && (
                      <CheckCircle className="w-6 h-6 text-teal-600" />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-4 mt-10 mb-6">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="cpf-secondary-button flex-1 px-6 py-5 bg-white border-2 border-slate-300 hover:border-slate-400 text-slate-800 text-lg font-bold rounded-2xl transition-all duration-300 flex items-center justify-center gap-3"
                  disabled={isLoading}
                >
                  <ArrowLeft className="w-6 h-6" />
                  <span>Back</span>
                </button>
              )}
              
              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="cpf-primary-button flex-1 px-6 py-5 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-black text-lg font-bold rounded-2xl transition-all duration-300 flex items-center justify-center gap-3"
                >
                  <span>Continue</span>
                  <ArrowRight className="w-6 h-6" />
                </button>
              ) : (
                <button
                  type="submit"
                  className="cpf-primary-button flex-1 px-6 py-5 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white text-lg font-bold rounded-2xl transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="cpf-button-spinner w-6 h-6 border-3 border-white/30 border-t-white rounded-full"></div>
                      <span>Completing...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-6 h-6" />
                      <span>Complete Profile</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </form>

          {/* Security Note */}
          <div className="mt-6 text-center p-4 bg-teal-50 border-2 border-teal-200 rounded-2xl shadow-sm">
            <p className="text-slate-700 text-sm font-medium flex items-center justify-center gap-2">
              <Shield className="w-4 h-4 text-teal-700" />
              Your information is secure and will only be used for your Producer Buddy account
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompleteProfile;
